import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EllipsisVertical } from "lucide-react";
import { POPOVER_GAP, POPOVER_VIEWPORT_MARGIN } from "./usePopoverPlacement";

/**
 * DS RowMenu — 테이블 행 ⋮ 버튼 + 팝오버 메뉴.
 *   케밥 아이콘: 4111:133533 행 마지막 셀 icon_regular 22px Text/Tertiary
 *   Popover 4116:134242 — 182w, white, p4, radius 12, shadow 2 6 12 rgba(1,1,1,.12), 항목 gap 4
 *   Popover-item — px10 py12 radius 8, SemiBold 14/22 Text/Secondary, danger = Red/50(System/Error)
 *
 * 팝오버는 body 포털 + position:fixed. 행 메뉴는 항상 `.ds-table-frame`(overflow:hidden) 안에 놓이므로
 * 부모 기준 absolute 로는 마지막 행(단일 행 포함)에서 메뉴가 잘려 동작하지 않는다.
 * 트리거 우측 정렬·아래 gap 4, 뷰포트 아래 여유가 팝오버 높이보다 작으면 위로. 스크롤/리사이즈 시 좌표 재계산.
 * 바깥 클릭/ESC 로 닫힘. 트리거·팝오버 클릭은 React 트리 전파를 막아 행 onClick 과 분리한다(포털도 React 트리로 전파).
 *
 * @param {object} props
 * @param {Array<{label: React.ReactNode, onClick: () => void, tone?: "danger", disabled?: boolean}>} props.items
 * @param {string} [props.className]
 * @param {string} [props.ariaLabel="더보기"]
 */
const DSRowMenu = ({ items = [], className = "", ariaLabel = "더보기" }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapperRef = useRef(null);
  const popRef = useRef(null);

  // 열린 동안 트리거 좌표 추적 — 팝오버가 커밋된 뒤(useLayoutEffect) 높이를 읽어 위/아래를 정한다
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const update = () => {
      const trigger = wrapperRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const popHeight = popRef.current ? popRef.current.offsetHeight : 0;
      const below = window.innerHeight - rect.bottom - POPOVER_GAP - POPOVER_VIEWPORT_MARGIN;
      const above = rect.top - POPOVER_GAP - POPOVER_VIEWPORT_MARGIN;
      const flipUp = below < popHeight && above > below;
      setPos({
        right: Math.max(0, window.innerWidth - rect.right),
        ...(flipUp
          ? { bottom: window.innerHeight - rect.top + POPOVER_GAP }
          : { top: rect.bottom + POPOVER_GAP }),
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleMouseDown = (e) => {
      if (wrapperRef.current && wrapperRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  // 행 onClick(상세 이동 등)과 겹치지 않도록 트리거·팝오버 클릭은 전파를 막는다
  const stop = (e) => e.stopPropagation();

  return (
    <div ref={wrapperRef} className={`ds-row-menu ${className}`} onClick={stop}>
      <button
        type="button"
        className={`ds-row-menu__trigger${open ? " ds-row-menu__trigger--open" : ""}`}
        onClick={() => setOpen((p) => !p)}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <EllipsisVertical size={22} />
      </button>
      {open &&
        createPortal(
          <div
            ref={popRef}
            className="ds-row-menu__popover"
            role="menu"
            // 첫 커밋은 좌표 계산 전 — 보이지 않게 두고 layout effect 가 pos 를 채운다
            style={pos || { visibility: "hidden", top: 0, right: 0 }}
            onClick={stop}
          >
            {items.map((item, idx) => (
              <button
                key={idx}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={`ds-row-menu__item${item.tone === "danger" ? " ds-row-menu__item--danger" : ""}`}
                onClick={() => {
                  setOpen(false);
                  item.onClick && item.onClick();
                }}
              >
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default React.memo(DSRowMenu);
